/* eslint-disable react/no-array-index-key */
import { useState } from 'react';

import { FaCrown } from 'react-icons/fa6';

import { useTheme } from 'styled-components';

import { UserSubscriptionTypesEnum } from '~/common/enums/user-subscription-types.enum';
import { Typography } from '~/common/components/Typograph';
import { Button } from '~/common/components/Button';

import {
  StyAdvantageous,
  StyAnnualNotice,
  StyCardContainer,
  StyComparisonPrice,
  StyPrimaryPriceRow,
  StyTitleContent,
  StyValueContent,
  StyWrapper,
} from './styles';

interface IPlan {
  id: string;
  externalId: string | null;
  title: string;
  value: string;
  recurrence: string;
  details: string;
  billingFrequency: 'MONTHLY' | 'YEARLY';
  comparisonValue?: string;
  comparisonCaption?: string;
  annualNotice?: string;
  hideAction?: boolean;
  unavailable?: boolean;
}

interface ISubscriptionCard {
  card: IPlan;
  current?: {
    subscription?: UserSubscriptionTypesEnum;
    billingFrequency?: 'MONTHLY' | 'YEARLY' | null;
    provider?: 'STRIPE' | 'PAGARME' | null;
  };
  handleChange: (plan: IPlan) => Promise<void>;
}

// Plan tier order for determining upgrade vs downgrade
const PLAN_ORDER: Record<string, number> = {
  [UserSubscriptionTypesEnum.FREE]: 0,
  [UserSubscriptionTypesEnum.BASIC]: 1,
  [UserSubscriptionTypesEnum.PREMIUM]: 2,
};

/* eslint-disable @typescript-eslint/no-shadow */
export const SubscriptionCard: React.FC<ISubscriptionCard> = ({
  card,
  current,
  handleChange,
}) => {
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isPremium = card.id === UserSubscriptionTypesEnum.PREMIUM;
  const isUnavailable = card.unavailable === true;
  const currentOrder =
    PLAN_ORDER[current?.subscription || UserSubscriptionTypesEnum.FREE] ?? 0;
  const cardOrder = PLAN_ORDER[card.id] ?? 0;
  const isBlockedYearlyChangeToMonthly =
    current?.billingFrequency === 'YEARLY' &&
    card.billingFrequency === 'MONTHLY' &&
    current?.subscription !== UserSubscriptionTypesEnum.FREE;
  const isBlockedYearlyDowngrade =
    current?.billingFrequency === 'YEARLY' &&
    current?.subscription !== UserSubscriptionTypesEnum.FREE &&
    card.id !== UserSubscriptionTypesEnum.FREE &&
    cardOrder < currentOrder;
  const isCurrentPlan =
    current?.subscription === card.id &&
    (card.id === UserSubscriptionTypesEnum.FREE ||
      current?.billingFrequency === card.billingFrequency);

  const getButtonText = (): string => {
    if (isUnavailable) return 'Indisponível';
    if (isBlockedYearlyChangeToMonthly) return 'Mudança indisponível';
    if (isBlockedYearlyDowngrade) return 'Downgrade indisponível';
    if (isCurrentPlan) return 'Plano Atual';

    if (card.id === UserSubscriptionTypesEnum.FREE) {
      if (
        current?.provider === 'PAGARME' &&
        current?.subscription &&
        current.subscription !== UserSubscriptionTypesEnum.FREE &&
        current.billingFrequency === 'MONTHLY'
      ) {
        return 'Cancelar assinatura';
      }

      return 'Gerenciar assinatura';
    }

    if (
      current?.subscription === card.id &&
      current?.billingFrequency !== card.billingFrequency
    ) {
      return 'Atualizar plano';
    }

    if (
      current?.subscription &&
      current.subscription !== UserSubscriptionTypesEnum.FREE
    ) {
      // User has an active paid subscription
      if (cardOrder > currentOrder) {
        return 'Fazer upgrade';
      }
      if (cardOrder < currentOrder) {
        return 'Fazer downgrade';
      }
    }

    return 'Assinar';
  };

  const handleSelect = (): void => {
    setLoading(true);
    handleChange(card).finally(() => {
      setLoading(false);
    });
  };

  return (
    <StyCardContainer
      $isPremium={isPremium}
      id={isPremium ? 'premium-plan' : ''}
    >
      {isPremium && (
        <StyAdvantageous>
          <Typography.Label size="x-small" color="neutral1000">
            MAIS VANTAJOSO
          </Typography.Label>
        </StyAdvantageous>
      )}
      <StyWrapper>
        <StyTitleContent>
          <Typography.Label
            size="x-large"
            color="neutral100"
            style={{
              fontWeight: 'bold',
            }}
          >
            {card.title}
          </Typography.Label>
          {isPremium && <FaCrown color={theme.yellow} size={20} />}
        </StyTitleContent>
        <StyValueContent>
          {!!card.comparisonValue && (
            <StyComparisonPrice>
              <Typography.Label
                size="small"
                color="neutral400"
                style={{ textDecoration: 'line-through' }}
              >
                {card.comparisonValue}
              </Typography.Label>
              {!!card.comparisonCaption && (
                <Typography.Label size="small" color="neutral400">
                  {card.comparisonCaption}
                </Typography.Label>
              )}
            </StyComparisonPrice>
          )}
          <StyPrimaryPriceRow>
            <Typography.Title
              size="h4"
              color="neutral100"
              style={{ fontWeight: 600 }}
            >
              {card.value}
            </Typography.Title>
            <Typography.Label size="medium" color="neutral100">
              {card.recurrence}
            </Typography.Label>
          </StyPrimaryPriceRow>
        </StyValueContent>
        <Typography.Label size="medium" color="neutral100">
          {card.details}
        </Typography.Label>
        {card.annualNotice && (
          <StyAnnualNotice>
            <Typography.Label size="small" color="neutral400">
              {card.annualNotice}
            </Typography.Label>
          </StyAnnualNotice>
        )}
      </StyWrapper>
      {!card.hideAction && (
        <Button
          backgroundColor={
            isCurrentPlan ? theme.transparent : theme.primary
          }
          textOrIconColor={
            isCurrentPlan ? theme.primary : theme.neutral1000
          }
          borderColor={isCurrentPlan ? theme.primary : theme.transparent}
          onClick={handleSelect}
          width="100%"
          height="40"
          justifyContent="center"
          loading={loading}
          loader="bar"
          disabled={
            isCurrentPlan ||
            isUnavailable ||
            isBlockedYearlyDowngrade ||
            isBlockedYearlyChangeToMonthly
          }
          text={getButtonText()}
        />
      )}
    </StyCardContainer>
  );
};
